import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import axios from "axios";
import { apiKey } from "../config/config";
import { toast } from "react-toastify";

/**
 * Customer Notes - Clean UI
 * - Main page: only "Add Note" button
 * - Modal: single searchable dropdown to select customer (latest completed contract only)
 * - Table: Customer | Note | Date | Actions (Edit/Delete)
 */
export default function NotesSheet({ lang }) {
  // --- Session / token / branch ---
  const adminRaw = sessionStorage.getItem("aman-admin");
  const adminObj = adminRaw ? JSON.parse(adminRaw) : null;
  const token = adminObj?.admin ? JSON.parse(adminObj.admin).token : null;
  const branchId = adminObj?.admin ? JSON.parse(adminObj.admin).id : null;

  // --- Data state ---
  const [customers, setCustomers] = useState([]);
  const [notes, setNotes] = useState([]);
  const [loadingNotes, setLoadingNotes] = useState(false);

  // --- Modal state ---
  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editNoteId, setEditNoteId] = useState(null);

  // --- Selection inside modal ---
  const [search, setSearch] = useState("");
  const [openDrop, setOpenDrop] = useState(false);
  const [selectedCustomerId, setSelectedCustomerId] = useState("");
  const [noteText, setNoteText] = useState("");
  const dropdownRef = useRef(null);

  // Helpers
  const getLatestContract = (contracts = []) => {
    if (!contracts || contracts.length === 0) return null;
    const sorted = [...contracts].sort((a, b) => {
      const da = new Date(a.date_contract || a.created_at || 0).getTime();
      const db = new Date(b.date_contract || b.created_at || 0).getTime();
      return db - da; // latest first
    });
    return sorted[0];
  };

  // Fetch customers
  const fetchCustomers = useCallback(() => {
    if (!branchId) return;
    axios
      .get(`${apiKey}/get-customers`, {
        params: { branch_id: branchId },
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => setCustomers(res.data.result || []))
      .catch((err) => console.error(err));
  }, [branchId, token]);

  // Fetch notes (branch-level, we filter per UI need if you add tabs later)
  const fetchNotes = useCallback(() => {
    if (!branchId) return;
    setLoadingNotes(true);
    axios
      .get(`${apiKey}/notes`, {
        params: { branch_id: branchId },
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => setNotes(res.data.result || []))
      .catch((err) => console.error(err))
      .finally(() => setLoadingNotes(false));
  }, [branchId, token]);

  useEffect(() => {
    fetchCustomers();
    fetchNotes();
  }, [fetchCustomers, fetchNotes]);

  // Build dropdown options: only customers with latest contract Completed (2)
  const completedOptions = useMemo(() => {
    return (customers || [])
      .map((c) => ({ ...c, _latest: getLatestContract(c.contracts || []) }))
      .filter((c) => Number(c?._latest?.contract_status) !== 2)
      .map((c) => ({
        value: c.customer_id,
        label: `${c.FullName} — #${c._latest?.contract_id * 9999}`,
        raw: c,
      }));
  }, [customers]);

  // Filter by search
  const filteredOptions = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return completedOptions;
    return completedOptions.filter(
      (o) => o.label.toLowerCase().includes(q) || String(o.value).includes(q)
    );
  }, [completedOptions, search]);

  // Selected customer object
  const selectedCustomer = useMemo(() => {
    if (!selectedCustomerId) return null;
    return (
      completedOptions.find(
        (o) => String(o.value) === String(selectedCustomerId)
      )?.raw || null
    );
  }, [selectedCustomerId, completedOptions]);

  // Click-outside to close dropdown
  useEffect(() => {
    function onDocClick(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpenDrop(false);
      }
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  // --- Modal control ---
  const openAddModal = () => {
    setIsEditing(false);
    setEditNoteId(null);
    setNoteText("");
    setSelectedCustomerId("");
    setSearch("");
    setShowModal(true);
  };

  const openEditModal = (row) => {
    setIsEditing(true);
    setEditNoteId(row.id);
    setNoteText(row.note || "");
    // try to sync customer picker so it shows the note's customer
    setSelectedCustomerId(String(row.customer_id));
    const labelForCustomer = completedOptions.find(
      (o) => String(o.value) === String(row.customer_id)
    )?.label;
    setSearch(labelForCustomer || "");
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setNoteText("");
    setIsEditing(false);
    setEditNoteId(null);
    setSelectedCustomerId("");
    setSearch("");
  };

  // --- Save (create/update) ---
  const saveNote = () => {
    if (!selectedCustomer) {
      toast.warn(lang.Select_customer_first || "Please select a customer");
      return;
    }
    const latest = getLatestContract(selectedCustomer.contracts || []);

    if (!noteText.trim()) {
      toast.warn(lang.Enter_note || "Enter a note");
      return;
    }

    const payload = {
      branch_id: branchId,
      customer_id: selectedCustomer.customer_id,
      contract_id: latest.contract_id, // IMPORTANT
      note: noteText.trim(),
    };

    if (!isEditing) {
      axios
        .post(`${apiKey}/notes`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        })
        .then(() => {
          toast.success(lang.Note_added || "Note added");
          closeModal();
          fetchNotes();
        })
        .catch((err) =>
          toast.error(err?.response?.data?.Message || "Error adding note")
        );
    } else {
      axios
        .put(`${apiKey}/notes/${editNoteId}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        })
        .then(() => {
          toast.success(lang.Updated || "Updated");
          closeModal();
          fetchNotes();
        })
        .catch((err) =>
          toast.error(err?.response?.data?.Message || "Error updating note")
        );
    }
  };

  // --- Delete ---
  const deleteNote = (id) => {
    axios
      .delete(`${apiKey}/notes/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(() => {
        toast.success(lang.Deleted || "Deleted");
        fetchNotes();
      })
      .catch((err) =>
        toast.error(err?.response?.data?.Message || "Error deleting note")
      );
  };

  // --- UI styles (subtle elevation, modern table look) ---
  const cardCls = "card border-0 shadow-sm";
  const sectionPad = "px-2 px-md-3 py-3";
  const tableCls = "table table-sm table-hover table-striped align-middle mb-0";
  const headCls = "table-light";

  return (
    <div>
      <main id="main" className="main">
        <div className="pagetitle">
          <h1 className="mb-0">{lang["Customer Notes"] || "Customer Notes"}</h1>
          <nav className="mt-2">
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                >
                  <path d="m21.743 12.331-9-10c-.379-.422-1.107-.422-1.486 0l-9 10a.998.998 0 0 0-.17 1.076c.16.361.518.593.913.593h2v7a1 1 0 0 0 1 1h3a1 1 0 0 0 1-1v-4h4v4a1 1 0 0 0 1 1h3a1 1 0 0 0 1-1v-7h2a.998.998 0 0 0 .743-1.669z"></path>
                </svg>
              </li>
              <li className="breadcrumb-item">
                {lang.Customer_Management || "Customer Management"}
              </li>
              <li className="breadcrumb-item active">
                {lang["Customer Notes"] || "Customer Notes"}
              </li>
            </ol>
          </nav>
        </div>

        <section className="section">
          {/* Action Card (only Add button on the page) */}
          <div className={cardCls}>
            <div
              className={`card-body d-flex justify-content-between align-items-center ${sectionPad}`}
            >
              <div></div>
              <button className="btn btn-primary" onClick={openAddModal}>
                + {lang["Add Note"] || "Add Note"}
              </button>
            </div>
          </div>

          {/* Notes Grid */}
          <div className={`${cardCls} mt-3`}>
            <div className={`${sectionPad}`}>
              <div className="table-responsive">
                <table className={tableCls}>
                  <thead
                    className={headCls}
                    style={{ position: "sticky", top: 0, zIndex: 1 }}
                  >
                    <tr>
                      <th style={{ width: "35%" }}>
                        {lang.Customer_Name || "Customer Name"}
                      </th>
                      <th style={{ width: "45%" }}>{lang.Note || "Note"}</th>
                      <th style={{ width: "12%" }}>{lang.date || "Date"}</th>
                      <th style={{ width: "8%" }}>
                        {lang.Actions || "Actions"}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {loadingNotes && (
                      <tr>
                        <td colSpan="4" className="text-center py-4">
                          {lang.Loading || "Loading..."}
                        </td>
                      </tr>
                    )}

                    {!loadingNotes && notes.length === 0 && (
                      <tr>
                        <td colSpan="4" className="text-center text-muted py-4">
                          {lang["No notes yet."] || "No notes yet."}
                        </td>
                      </tr>
                    )}

                    {!loadingNotes &&
                      notes.map((n) => {
                        const c = customers.find(
                          (x) => String(x.customer_id) === String(n.customer_id)
                        );
                        return (
                          <tr key={n.id}>
                            <td className="fw-medium">
                              {c?.FullName || `#${n.customer_id}`}
                              <div className="small text-muted">
                                ID:
                                {c?.ValidNationalID
                                  ? ` ${c.ValidNationalID}`
                                  : ""}
                              </div>
                            </td>
                            <td style={{ whiteSpace: "pre-wrap" }}>{n.note}</td>
                            <td>
                              {n.noted_at
                                ? new Date(n.noted_at).toLocaleString()
                                : "-"}
                            </td>
                            <td>
                              <div className="btn-group btn-group-sm">
                                <button
                                  className="btn btn-outline-secondary"
                                  title={lang.Edit || "Edit"}
                                  onClick={() => openEditModal(n)}
                                >
                                  ✏️
                                </button>
                                <button
                                  className="btn btn-outline-danger"
                                  title={lang.Delete || "Delete"}
                                  onClick={() => deleteNote(n.id)}
                                >
                                  🗑️
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Add/Edit Modal (contains the SINGLE searchable select) */}
          <div
            className={`modal fade ${showModal ? "show d-block" : ""}`}
            tabIndex="-1"
            role="dialog"
            aria-modal={showModal ? "true" : "false"}
            style={{
              background: showModal ? "rgba(0,0,0,.35)" : "transparent",
            }}
            onClick={(e) => {
              if (e.target.classList.contains("modal")) closeModal();
            }}
          >
            <div className="modal-dialog modal-lg modal-dialog-centered">
              <div className="modal-content shadow">
                <div className="modal-header">
                  <h5 className="modal-title">
                    {isEditing
                      ? lang["Edit Note"] || "Edit Note"
                      : lang["Add Note"] || "Add Note"}
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={closeModal}
                  ></button>
                </div>
                <div className="modal-body">
                  <div className="row g-3">
                    {/* SINGLE Searchable Select */}
                    <div className="col-12">
                      <label className="form-label">
                        {lang["Search / Select Customer"] ||
                          "Search / Select Customer"}
                      </label>
                      <div className="position-relative" ref={dropdownRef}>
                        <div className="input-group">
                          <span className="input-group-text">🔎</span>
                          <input
                            className="form-control"
                            placeholder={
                              lang["Type to search by name or ID..."] ||
                              "Type to search by name or ID..."
                            }
                            value={search}
                            onChange={(e) => {
                              setSearch(e.target.value);
                              setOpenDrop(true);
                            }}
                            onFocus={() => setOpenDrop(true)}
                          />
                        </div>
                        {openDrop && (
                          <div
                            className="list-group position-absolute w-100 mt-1 shadow-sm rounded overflow-auto"
                            style={{
                              maxHeight: 280,
                              zIndex: 1050,
                              background: "var(--bs-body-bg)",
                            }}
                          >
                            {filteredOptions.length === 0 && (
                              <div className="list-group-item text-muted">
                                {lang.No_results || "No results"}
                              </div>
                            )}
                            {filteredOptions.map((o) => (
                              <button
                                key={o.value}
                                type="button"
                                className={`list-group-item list-group-item-action d-flex justify-content-between ${
                                  String(selectedCustomerId) === String(o.value)
                                    ? "active"
                                    : ""
                                }`}
                                onClick={() => {
                                  setSelectedCustomerId(String(o.value));
                                  setSearch(o.label);
                                  setOpenDrop(false);
                                }}
                              >
                                <span>{o.label}</span>
                                <small className="text-muted ms-3">
                                  ID: {o.value}
                                </small>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                      {selectedCustomer && (
                        <div className="form-text mt-1">
                          {lang.Selected || "Selected"}:{" "}
                          <strong>{selectedCustomer.FullName}</strong> |{" "}
                          {lang.contract || "Contract"}:{" "}
                          <strong>
                            #
                            {
                              getLatestContract(selectedCustomer.contracts)
                                ?.contract_id
                            }
                          </strong>
                        </div>
                      )}
                    </div>

                    {/* Note textarea */}
                    <div className="col-12">
                      <label className="form-label">
                        {lang.Note || "Note"}
                      </label>
                      <textarea
                        className="form-control"
                        rows={4}
                        value={noteText}
                        onChange={(e) => setNoteText(e.target.value)}
                        placeholder={lang.Type_here || "Type here..."}
                      />
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button className="btn btn-secondary" onClick={closeModal}>
                    {lang.Cancel || "Cancel"}
                  </button>
                  <button className="btn btn-primary" onClick={saveNote}>
                    {isEditing ? lang.Update || "Update" : lang.Save || "Save"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}
